import cardValidator from 'card-validator';
import React, { useEffect, useRef, useState } from 'react';
import { ErrorMessage, useForm } from 'react-hook-form';
import { getTranslate } from 'react-localize-redux';
import { connect, Dispatch } from 'react-redux';
import FlipDishCopyRight from '../../FlipDishCopyRight/index.weborder';
import { Alert, Grid, SubmitButton, TextField, Typography } from '../../../../ui';
import { paymentActions } from '../../../redux';
import { paymentApiServices } from '../../../services';

const isHttps = window.location.protocol === 'https:';

type CreditCardFormProps = {
  onSuccess?(): void;
  onFail?(): void;
};

type Props = CreditCardFormProps & MapStateToProps & MapDispatchToProps;

const CreditCardForm = (props: Props) => {
  const { translate, creditCardPaymentAdded, cancelAddCardBridge, onSuccess, onFail } = props;

  const { register, errors, handleSubmit, formState, control } = useForm<{
    ccNumber: string;
    expDate: string;
    cvvNumber: string;
  }>({ validateCriteriaMode: 'all' });

  // Read the formState before render to subscribe the form state through Proxy
  const { isSubmitting } = formState;

  const [validNumber, setValidNumber] = useState<cardValidator.validNumber>();
  const [submitError, setSubmitError] = useState<Error>();

  const [formattedCcNumber, setFormattedCcNumber] = useState('');
  const [formattedExpDate, setFormattedExpDate] = useState('');

  //#region CANCEL ADDING CARD FLOW
  const isCancelFlag = useRef(true);
  useEffect(() => {
    return () => {
      if (isCancelFlag.current) {
        cancelAddCardBridge();
      }
    };
  }, []);
  //#endregion

  const onSubmit = handleSubmit(async (props) => {
    try {
      // TODO LOCK ROUTER

      const { month, year } = cardValidator.expirationDate(props.expDate);

      const digitMonth = parseInt(month);
      const fourDigitYear = year.length === 4 ? parseInt(year) : parseInt(`20${year}`);

      const paymentAccountId = await paymentApiServices.createCard({
        cardNumber: props.ccNumber,
        expMonth: digitMonth,
        expYear: fourDigitYear,
        cvv: props.cvvNumber
      });

      isCancelFlag.current = false;

      creditCardPaymentAdded({
        cardNumber: props.ccNumber,
        paymentAccountId
      });

      onSuccess && onSuccess();
    } catch (error) {
      setSubmitError(error);
      onFail && onFail();
    } finally {
      // TODO UNLOCK ROUTER
    }
  });

  return (
    <form onSubmit={onSubmit} noValidate>
      <Grid container spacing={3} alignItems="flex-end">
        <Grid item xs={12}>
          <TextField
            dataFd="credit-card-number"
            id="creditCard"
            name="ccNumber"
            fullWidth
            label={translate('Card_number')}
            autoFocus
            autoComplete="cc-number"
            placeholder="•••• •••• •••• ••••"
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*'
            }}
            value={formattedCcNumber}
            onChange={(e) => {
              const v = cardValidator.number(e.target.value);
              setValidNumber(v);
              // console.log('[PAYMENT] onChange', v);
              const value: string = e.target.value.replace(/\D/g, '');
              if (v.card) {
                const formatted = Array.from(value)
                  // insert spaces where r gaps
                  .reduce((agg, c, idx) => {
                    if (v.card.gaps.includes(idx)) {
                      return `${agg} ${c}`;
                    }
                    return `${agg}${c}`;
                  }, '')
                  // don't allow input more than cards max length + spaces
                  .slice(0, Math.max(...v.card.lengths) + v.card.gaps.length);

                setFormattedCcNumber(formatted);
              } else {
                setFormattedCcNumber(value);
              }

              // control.fieldsRef.current['expDate'].ref.focus()
            }}
            inputRef={register({
              validate: (value) => {
                const v = cardValidator.number(value);
                // console.log('[PAYMENT] validate', value, v);
                return v.isValid;
              }
            })}
            error={!!errors.ccNumber}
          />
        </Grid>

        <Grid item xs={6} md={3}>
          <TextField
            dataFd="credit-card-expiry"
            id="expiryDate"
            name="expDate"
            fullWidth
            label={translate('Exp_date')}
            autoComplete="cc-exp"
            placeholder="MM/YY"
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*'
            }}
            value={formattedExpDate}
            onChange={(e) => {
              // const v = cardValidator.expirationDate(e.target.value);
              // console.log('[PAYMENT] onChange', v);

              const value: string = e.target.value.replace(/\D/g, '');
              const formatted = Array.from(value)
                // insert slash after 2nd char
                .reduce((agg, c, idx) => {
                  if (idx === 2) {
                    return `${agg}/${c}`;
                  }
                  return `${agg}${c}`;
                }, '')
                // don't allow input more than MM/YYYY
                .slice(0, 7);

              setFormattedExpDate(formatted);
            }}
            inputRef={register({
              validate: (value) => {
                const v = cardValidator.expirationDate(value);
                // console.log('[PAYMENT] validate', v);
                return v.isValid;
              }
            })}
            error={!!errors.expDate}
          />
        </Grid>

        <Grid item xs={6} md={2}>
          <TextField
            dataFd="credit-card-cvv"
            id="cvvNumber"
            name="cvvNumber"
            fullWidth
            label={translate('CVV_number')}
            autoComplete="cc-csc"
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*'
            }}
            inputRef={register({
              validate: (value) => {
                const maxLen = validNumber && validNumber.card.code && validNumber.card.code.size;
                const result = cardValidator.cvv(value, maxLen);
                return result.isValid;
              }
            })}
            error={!!errors.cvvNumber}
          />
        </Grid>

        {!isHttps && (
          <Grid item xs={12}>
            <Alert color="warning" align="center" aria-live="polite">
              {translate('Cards_cannot_added')}
            </Alert>
          </Grid>
        )}

        {Object.keys(errors).length > 0 && (
          // A11y: needs 'aria-describedby="error-*"' which is inside TextField.helperText
          <Grid item xs={12}>
            <Alert color="error" aria-live="polite">
              <Typography color="inherit" variant="subtitle1" paragraph>
                {translate('Error')}
              </Typography>

              <ErrorMessage
                errors={errors}
                name="ccNumber"
                as="p"
                message={translate('Please_check_the_credit_card_number') as string}
              />

              <ErrorMessage
                errors={errors}
                name="expDate"
                as="p"
                message={translate('Please_check_the_card_expiration_date') as string}
              />

              <ErrorMessage
                errors={errors}
                name="cvvNumber"
                as="p"
                message={translate('CVV_number') as string}
              />
            </Alert>
          </Grid>
        )}

        {submitError && (
          <Grid item xs={12}>
            <Alert color="error" aria-live="polite">
              <Typography color="inherit" variant="subtitle1" paragraph>
                {translate('Error')}
              </Typography>

              {translate(submitError.message, undefined, {
                missingTranslationMsg: submitError.message
              })}
            </Alert>
          </Grid>
        )}
        <Grid item xs={12}>
          <FlipDishCopyRight />
        </Grid>
        <Grid item sm={12} container justify="center">
          <Grid item sm={4}>
            <SubmitButton dataFd="Add_card_button" loading={isSubmitting}>
              {translate('Add_card')}
            </SubmitButton>
          </Grid>
        </Grid>
      </Grid>
    </form>
  );
};

export { CreditCardForm };

type MapStateToProps = ReturnType<typeof mapStateToProps>;
const mapStateToProps = (state: WebOrder.State) => {
  return {
    translate: getTranslate(state.locale)
  };
};

type MapDispatchToProps = ReturnType<typeof mapDispatchToProps>;
const mapDispatchToProps = (dispatch: Dispatch<WebOrder.State>) => {
  return {
    creditCardPaymentAdded: (props: paymentActions.CreditCardPaymentAddedProps) => {
      dispatch(paymentActions.creditCardPaymentAdded(props));
    },
    cancelAddCardBridge: () => {
      dispatch(paymentActions.cancelAddBridge());
    }
  };
};

export default connect<MapStateToProps, MapDispatchToProps, CreditCardFormProps>(
  mapStateToProps,
  mapDispatchToProps
)(CreditCardForm);
